import { events } from "@constants/tracking";
import CustomKeyboardAvoidingView, {
	type CustomKeyboardAvoidingViewRef,
} from "@features/account/components/CustomKeyboardAvoidingView";
import { isIOS, isWeb } from "@helpers/platform";
import { useAppStore } from "@hooks/useAppStore";
import { useAppTheme } from "@hooks/useAppTheme";
import { useDevice } from "@hooks/useDevice";
import type {
	BaseItemSchema,
	ItemResponse,
	OriginalItemTypes,
} from "@memorang/types";
import Box from "@memorang/ui/components/Box";
import { FAB } from "@memorang/ui/components/FAB";
import { trackCustomEvent } from "analytics";
import FlightCalculatorDialog from "components/FlightCalculator/FlightCalculatorDialog";
import { useEffect, useRef, useState } from "react";
import { Dimensions } from "react-native";
import { Caption, Portal } from "react-native-paper";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import {
	FAB_BOTTOM_POSITION,
	SESSION_EVENTS,
	sessionMaxWidth,
} from "../constants";
import StudySessionFeedbackContainer from "../containers/StudySessionFeedbackContainer";
import composeSessionMd from "../helpers/compose-session-md";
import { getIsSlidesHasPassage, getSlidesData } from "../helpers/session";
import useSessionStore from "../hooks/useSessionStore";
import PassageStemContainer from "./PassageStemContainer";
import { StemContainer } from "./StemContainer";

interface Props {
	children?: React.ReactNode;
	item: BaseItemSchema;
	response: ItemResponse;
	toggleShowReadMore?: () => void;
	originalItemType: OriginalItemTypes;
	handleHighlightedTextPress?: () => void;
	footer?: React.ReactNode;
	instructions?: string;
	selectedChoiceIds?: string[];
}

const screenWidth = Dimensions.get("window").width;

const CalculatorFAB = ({
	handleShowCalculatorDialog,
}: {
	handleShowCalculatorDialog: () => void;
}) => {
	const { bottom } = useSafeAreaInsets();
	return (
		<FAB
			icon={"calculator"}
			label={"Calculator"}
			onPress={handleShowCalculatorDialog}
			style={{
				position: "absolute",
				bottom: FAB_BOTTOM_POSITION + (isIOS ? bottom - 12 : 0),
				right: 24,
			}}
		/>
	);
};
const ItemWrapper = ({
	children,
	item,
	response,
	toggleShowReadMore,
	originalItemType,
	handleHighlightedTextPress,
	footer,
	instructions,
	selectedChoiceIds,
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
}: Props) => {
	const {
		id,
		content,
		stimulus: parentStimulus,
		recordType,
		stimulusMetadata,
	} = item;

	const [showCalculatordialog, setShowCalculatordialog] = useState(false);

	const parentStimulusType = stimulusMetadata?.type;
	const parentTitle =
		parentStimulus?.passage?.title || parentStimulus?.className;
	const currentStimulusItemIndex = item.currentStimulusItemIndex;
	const numTotalStimulusItems = item.numTotalStimulusItems;
	const parentInstruction = parentStimulus?.instruction;
	const parentSlides = parentStimulus?.slides;
	const parentImages = parentStimulus?.media?.filter(
		(media) => media.type === "image" || media.type === "IMAGE",
	);
	const parentProfessor = parentStimulus?.professor;
	const skill = parentStimulus?.metadata?.skill;

	const slideshowData = getSlidesData(parentSlides);

	const isImageChallengeApp = useAppStore(
		(state) => state.app.isImageChallengeApp,
	);
	const showFlightCalculator = useAppStore(
		(state) => state.sessionView?.showFlightCalculator,
	);

	const media = content.stem?.media?.length
		? content.stem?.media
		: content.media;

	const audio = media?.find(
		(media) => media.type === "audio" || media.type === "AUDIO",
	)?.url;
	const video = media?.find(
		(media) => media.type === "video" || media.type === "VIDEO",
	)?.url;
	const images = media?.filter(
		(media) => media.type === "image" || media.type === "IMAGE",
	);

	const isSlidesHasPassage = getIsSlidesHasPassage(parentSlides);

	const parentAudio = parentStimulus?.media?.find(
		(media) => media.type === "audio" || media.type === "AUDIO",
	)?.url;

	const parentStem =
		item.stimulus?.stem?.text ||
		composeSessionMd({
			content: item.stimulus?.passage,
			highlights: item.content.highlights,
			insertText: item.content.insertSentence,
			paragraphNumber: item.content.paragraphNumber,
			choices: item.response.type === "choice" ? item.response.choices : [],
			selectedChoiceIds,
			shouldFormat: isWeb,
		});

	const itemStates = useSessionStore((state) => state.itemStates);
	const theme = useAppTheme();

	const inReviewMode = useSessionStore((state) => state.inReviewMode);
	const itemState = itemStates?.get(id!);
	const isAnswered = itemState?.answered;

	const updateItemAttempts = useSessionStore(
		(state) => state.updateItemAttempts,
	);

	const updateHighlightedItems = useSessionStore(
		(state) => state.updateHighlightedItems,
	);
	const blockStates = useSessionStore((state) => state.blockStates);
	const currentBlockIndex = useSessionStore((state) => state.currentBlockIndex);
	const currentBlockState = blockStates.get(currentBlockIndex);
	const highlightedItems = currentBlockState?.highlightedItems;
	const addSessionEvent = useSessionStore((state) => state.addSessionEvent);
	const { isMobile, isMediumWindowSize } = useDevice();

	const isSummativeTest = useSessionStore((state) => state.isSummativeTest);

	const updateItemStates = useSessionStore((state) => state.updateItemStates);

	const multiSelect = Boolean(
		response?.type === "choice" &&
			response.selectionType === "multiple" &&
			response.maxCorrectChoices &&
			response.maxCorrectChoices > 1,
	);

	const shouldRenderHorizontalLayout =
		recordType === "SCENARIO" && isWeb && !isMobile && !isMediumWindowSize;

	const handleStoreHtml = (html: string) => {
		const hasHighlight = html?.includes("<mark");
		const previousHighlight = highlightedItems?.get(id!);
		const hadHighlight = previousHighlight?.includes("<mark");

		if (isSummativeTest && hasHighlight && !hadHighlight) {
			addSessionEvent(id!, {
				name: SESSION_EVENTS.TEXT_HIGHLIGHTED,
				props: {
					htmlLength: html?.length || 0,
				},
			});
		}

		updateHighlightedItems(id!, html);
	};

	const handleSelectChoice = (choiceId: string, crossingOut?: boolean) => {
		if (isImageChallengeApp) {
			updateItemAttempts({
				itemId: `${id}`,
				selectedChoiceId: choiceId,
				crossOut: crossingOut,
			});
		} else {
			updateItemStates({
				itemId: id!,
				selectedChoiceId: choiceId,
				multiSelect,
				crossOut: crossingOut,
			});
		}
	};

	const handleClickInsertableText = (choiceId: string) => {
		trackCustomEvent({
			eventName: events.sessionItemTextInserted,
			choiceId,
			itemId: id!,
		});
		handleSelectChoice(choiceId);
	};

	const showTTS = parentStimulusType === "LISTENING";

	const shouldShowCalculatorFAB =
		!(isSummativeTest || isAnswered || inReviewMode) && showFlightCalculator;

	const hasPassage = parentStem || parentTitle;

	const customKeyboardAvoidingViewRef =
		useRef<CustomKeyboardAvoidingViewRef | null>(null);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (customKeyboardAvoidingViewRef.current) {
			customKeyboardAvoidingViewRef.current.scrollTo(0);
		}
	}, [id]);

	return (
		<Box
			gap={16}
			flexGrow={1}
			height="100%"
			paddingHorizontal={isWeb && isSummativeTest && !isMobile ? 24 : 0}
			width={isWeb ? "auto" : screenWidth}
			justifyContent="space-between"
			paddingBottom={isSummativeTest ? 0 : 88}
		>
			<Box
				flex={1}
				flexDirection={shouldRenderHorizontalLayout ? "row" : "column"}
			>
				{hasPassage ? (
					<Box flex={shouldRenderHorizontalLayout ? 1 : undefined}>
						<PassageStemContainer
							stem={parentStem || ""}
							audio={parentAudio}
							toggleShowReadMore={toggleShowReadMore}
							id={id}
							index={currentStimulusItemIndex}
							parentType={parentStimulusType!}
							handleStoreHtml={handleStoreHtml}
							handleClickInsertableText={handleClickInsertableText}
							isSummativeTest={isSummativeTest}
							numPassageItems={numTotalStimulusItems}
							parentTitle={parentTitle}
							slideshowData={slideshowData}
							parentInstruction={
								typeof parentInstruction === "string"
									? parentInstruction
									: parentInstruction?.text
							}
							professor={parentProfessor}
							slides={parentSlides}
							isSlidesHasPassage={isSlidesHasPassage}
							skill={skill!}
							disableInsertableText={isAnswered}
							parentImages={parentImages}
						/>
					</Box>
				) : null}
				{hasPassage && !isMobile && !isMediumWindowSize && (
					<Box
						width={1}
						height={"100%"}
						backgroundColor={theme.colors.outlineVariant}
						marginHorizontal={16}
					/>
				)}
				<CustomKeyboardAvoidingView
					ref={customKeyboardAvoidingViewRef}
					showsVerticalScrollIndicator={false}
					style={{
						flex: 1,
					}}
					contentContainerStyle={{
						flexGrow: 1,
						paddingBottom: (isAnswered && !isWeb) || isSummativeTest ? 300 : 64,
						paddingTop: isSummativeTest ? 16 : 0,
						gap: 16,
					}}
				>
					<Box
						gap={16}
						maxWidth={sessionMaxWidth.formative}
						alignSelf={isMediumWindowSize ? "center" : undefined}
						width={isMediumWindowSize ? "100%" : undefined}
					>
						<StemContainer
							item={content}
							video={video}
							audio={audio}
							key={id}
							handleStoreHtml={handleStoreHtml}
							isSummativeTest={isSummativeTest}
							itemId={id!}
							showTTS={showTTS}
							handleHighlightedTextPress={handleHighlightedTextPress}
							originalItemType={originalItemType}
							images={images}
						/>
						<Box gap={8}>
							{instructions && (
								<Caption
									style={{
										paddingHorizontal: isMobile ? 16 : 0,
									}}
								>
									{instructions}
								</Caption>
							)}
							{children}
						</Box>
						{shouldShowCalculatorFAB && isWeb && (
							<Portal>
								<CalculatorFAB
									handleShowCalculatorDialog={() =>
										setShowCalculatordialog(true)
									}
								/>
							</Portal>
						)}

						{isWeb && (
							<StudySessionFeedbackContainer
								hasParentStem={Boolean(shouldRenderHorizontalLayout)}
							/>
						)}
					</Box>
				</CustomKeyboardAvoidingView>
			</Box>
			{footer}
			{shouldShowCalculatorFAB && !isWeb && (
				<CalculatorFAB
					handleShowCalculatorDialog={() => setShowCalculatordialog(true)}
				/>
			)}
			{showCalculatordialog && (
				<FlightCalculatorDialog
					dialogVisible={showCalculatordialog}
					hideDialog={() => setShowCalculatordialog(false)}
				/>
			)}
		</Box>
	);
};

export default ItemWrapper;
